import { FormikProvider, useFormik } from 'formik';
import Delta from 'quill-delta';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	Button,
	DeleteIcon,
	GraphQLErrorMessage,
	Label,
	Modal,
	Popover,
	QuillInput,
	QuillText,
	RadioButton,
	Select,
	UserSelect,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { READINESS_OPTIONS, readinessLabelKey } from './successorReadiness';
import { useKeyPositionOptions } from './useKeyPositionOptions';

const ReadinessGroup = styled(Box)`
	&& label {
		color: ${tokens.text.primary};
		opacity: 1;

		&:before {
			border-color: ${tokens.text.primary};
		}
	}
`;

type ExistingNomination = {
	id: string;
	user: { id: string; firstName: string; lastName: string };
	readiness: graphql.SuccessorReadiness;
	motivation: Delta;
	viewerCanArchive: boolean;
};

// "Voeg mogelijke opvolger toe": pick a (key) position, a candidate, a
// readiness and write a motivation. From the talent review sidebar the job
// title is already the person's key role. Opening an existing successor
// prefills that nomination so the nominator can edit it or withdraw it.
export default function NominateSuccessorModal(props: {
	jobTitleID?: string;
	nomination?: ExistingNomination;
	onClose: () => void;
	onNominated: (jobTitleID: string) => void;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { sections, loading: titlesLoading } = useKeyPositionOptions();
	const nomination = props.nomination;

	const [nominate, { loading: nominating, error }] =
		graphql.useNominateJobPositionSuccessorMutation({
			refetchQueries: ['JobPositionSuccessors', 'JobTitleSuccessors'],
		});
	const [updateNomination, { loading: updating, error: updateError }] =
		graphql.useUpdateJobPositionSuccessorMutation({
			refetchQueries: ['JobPositionSuccessors', 'JobTitleSuccessors'],
		});
	const [archive, { loading: archiving, error: archiveError }] =
		graphql.useArchiveJobPositionSuccessorMutation({
			refetchQueries: ['JobPositionSuccessors', 'JobTitleSuccessors'],
		});
	const saving = nominating || updating;
	// Opening your own nomination edits it. Someone else's stays a new nomination.
	const editingOwn = !!nomination?.viewerCanArchive;

	const formik = useFormik<{
		jobTitleID?: string;
		userID?: string;
		readiness: graphql.SuccessorReadiness;
		motivation: Delta;
	}>({
		initialValues: {
			jobTitleID: props.jobTitleID,
			userID: nomination?.user.id,
			readiness: nomination?.readiness ?? graphql.SuccessorReadiness.ReadyNow,
			motivation: nomination ? new Delta(nomination.motivation) : new Delta(),
		},
		onSubmit: async (values) => {
			if (!values.jobTitleID || !values.userID) return;

			try {
				if (editingOwn && nomination) {
					await updateNomination({
						variables: {
							input: {
								id: nomination.id,
								jobTitleID: values.jobTitleID,
								userID: values.userID,
								readiness: values.readiness,
								motivation: values.motivation,
							},
						},
					});
				} else {
					await nominate({
						variables: {
							input: {
								jobTitleID: values.jobTitleID,
								userID: values.userID,
								readiness: values.readiness,
								motivation: values.motivation,
							},
						},
					});
				}
				addToast(t('analytics.talentReview.succession.modal.success'), {
					appearance: 'success',
					autoDismiss: true,
				});
				props.onNominated(values.jobTitleID);
			} catch {
				// error state is shown via GraphQLErrorMessage
			}
		},
	});

	const canRemove = editingOwn;
	const canSubmit =
		!!formik.values.jobTitleID &&
		!!formik.values.userID &&
		!QuillText.isEmpty(formik.values.motivation) &&
		(!editingOwn || formik.dirty);
	const graphQLError = (archiveError ?? updateError ?? error)?.graphQLErrors[0];

	const handleRemove = async () => {
		if (!nomination) return;

		try {
			await archive({ variables: { input: { id: nomination.id } } });
			addToast(t('analytics.talentReview.succession.modal.removed'), {
				appearance: 'success',
				autoDismiss: true,
			});
			props.onClose();
		} catch {
			// error state is shown via GraphQLErrorMessage
		}
	};

	const selectedUser =
		nomination && formik.values.userID === nomination.user.id
			? {
					value: nomination.user.id,
					title: `${nomination.user.firstName} ${nomination.user.lastName}`,
				}
			: undefined;

	return (
		<Popover visible center onDismiss={props.onClose}>
			<Modal>
				<Modal.Header
					title={t('analytics.talentReview.succession.modal.title')}
					onClose={props.onClose}
				/>
				<Modal.Contents>
					<FormikProvider value={formik}>
						<Box mb={3}>
							<Label mb={2}>{t('analytics.talentReview.succession.modal.jobTitle')}</Label>
							{titlesLoading ? (
								<Select.Skeleton />
							) : (
								<Select
									options={sections}
									selectedValue={formik.values.jobTitleID}
									placeholder={t('analytics.talentReview.succession.modal.jobTitlePlaceholder')}
									onChange={(value) => formik.setFieldValue('jobTitleID', value)}
								/>
							)}
						</Box>
						<Box mb={3}>
							<Label mb={2}>{t('analytics.talentReview.succession.modal.candidate')}</Label>
							<UserSelect
								autofocus={!!props.jobTitleID && !nomination}
								selectedUserID={formik.values.userID}
								selectedOption={selectedUser}
								setSelected={(userID) => formik.setFieldValue('userID', userID)}
								includeViewer={false}
								placeholder={t('analytics.talentReview.succession.modal.candidatePlaceholder')}
							/>
						</Box>
						<ReadinessGroup mb={3}>
							<Label mb={2}>{t('analytics.talentReview.succession.modal.readiness')}</Label>
							{READINESS_OPTIONS.map((readiness) => (
								<RadioButton
									key={readiness}
									id={`readiness-${readiness}`}
									name="readiness"
									value={readiness}
									label={t(readinessLabelKey(readiness))}
									checked={formik.values.readiness === readiness}
									onChange={() => formik.setFieldValue('readiness', readiness)}
								/>
							))}
						</ReadinessGroup>
						<Box mb={2}>
							<Label mb={2}>{t('analytics.talentReview.succession.modal.motivation')}</Label>
							<QuillInput
								value={formik.values.motivation}
								minHeight={120}
								placeholder={t('analytics.talentReview.succession.modal.motivationPlaceholder')}
								onChange={(value) => formik.setFieldValue('motivation', value)}
							/>
						</Box>
						{graphQLError && <GraphQLErrorMessage error={graphQLError} />}
					</FormikProvider>
				</Modal.Contents>
				<Modal.Footer>
					{canRemove && (
						<Button
							mr="auto"
							type="destructive"
							icon={<DeleteIcon />}
							disableSuccess
							loading={archiving}
							disabled={saving}
							title={t('analytics.talentReview.succession.modal.remove')}
							onClick={handleRemove}
						/>
					)}
					<Button
						disabled={saving || archiving}
						title={t('common.cancel')}
						type="secondary"
						onClick={props.onClose}
					/>
					<Button
						ml={2}
						loading={saving}
						disabled={!canSubmit || archiving}
						disableSuccess
						title={
							editingOwn ? t('common.save') : t('analytics.talentReview.succession.modal.submit')
						}
						onClick={() => formik.submitForm()}
					/>
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
